import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Heading2, Italic, Link2, List, ListOrdered, Quote, Strikethrough } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { RichText } from '@cw/shared';
import { cn } from '@/lib/utils';

const extensions = (placeholder?: string) => [
  StarterKit.configure({ heading: { levels: [2, 3] }, link: false, underline: false }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    protocols: ['http', 'https', 'mailto'],
    HTMLAttributes: { rel: 'noreferrer', target: '_blank' },
  }),
  ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
];

const proseClass =
  'prose-cw text-[14px] leading-relaxed text-fg focus:outline-none [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-[17px] [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:text-[15px] [&_h3]:font-semibold [&_p]:my-1.5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-line-strong [&_blockquote]:pl-3 [&_blockquote]:text-fg-muted [&_a]:text-fg [&_a]:underline [&_a]:underline-offset-4 [&_code]:rounded [&_code]:bg-press [&_code]:px-1 [&_code]:text-[13px] [&_.is-editor-empty:first-child::before]:pointer-events-none [&_.is-editor-empty:first-child::before]:float-left [&_.is-editor-empty:first-child::before]:h-0 [&_.is-editor-empty:first-child::before]:text-fg-subtle [&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]';

function ToolbarButton({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex size-7 items-center justify-center rounded-[7px] text-fg-muted transition-colors hover:bg-hover hover:text-fg [&_svg]:size-[15px]',
        active && 'bg-press text-fg',
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const link = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Endereço do link', prev ?? 'https://');
    if (url === null) return;
    if (!url) editor.chain().focus().unsetLink().run();
    else if (/^(https?:|mailto:)/i.test(url))
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  return (
    <div className="flex flex-wrap gap-0.5 border-b border-line px-2 py-1.5">
      <ToolbarButton
        label="Título"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 />
      </ToolbarButton>
      <ToolbarButton
        label="Negrito"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold />
      </ToolbarButton>
      <ToolbarButton
        label="Itálico"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic />
      </ToolbarButton>
      <ToolbarButton
        label="Riscado"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough />
      </ToolbarButton>
      <ToolbarButton
        label="Lista"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List />
      </ToolbarButton>
      <ToolbarButton
        label="Lista numerada"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered />
      </ToolbarButton>
      <ToolbarButton
        label="Citação"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote />
      </ToolbarButton>
      <ToolbarButton label="Link" active={editor.isActive('link')} onClick={link}>
        <Link2 />
      </ToolbarButton>
    </div>
  );
}

/**
 * Editor rico (TipTap). Salva sozinho: chama `onSave` com o documento 1,2 s depois da última digitação
 * e ao sair do campo. O conteúdo é JSON validado pelo servidor; nunca HTML.
 */
export function RichEditor({
  value,
  onSave,
  onChange,
  ariaLabel,
  placeholder,
  editable = true,
  className,
  minHeight = 120,
  compact = false,
  showToolbar = true,
  onSubmit,
  autoFocus = false,
}: {
  value: RichText | null;
  onSave?: (doc: RichText) => void;
  onChange?: (doc: RichText) => void;
  ariaLabel?: string;
  placeholder?: string;
  editable?: boolean;
  className?: string;
  minHeight?: number;
  compact?: boolean;
  showToolbar?: boolean;
  onSubmit?: () => void;
  autoFocus?: boolean;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef(onSave);
  const latestChange = useRef(onChange);
  const latestSubmit = useRef(onSubmit);
  // Último conteúdo salvo: só salva de novo se algo mudou de verdade (normalizações do editor não contam).
  const saved = useRef<string | null>(null);
  useEffect(() => {
    latest.current = onSave;
    latestChange.current = onChange;
    latestSubmit.current = onSubmit;
  });
  const flush = (editor: Editor) => {
    clearTimeout(timer.current);
    timer.current = undefined;
    const doc = editor.getJSON() as RichText;
    const json = JSON.stringify(doc);
    if (json === saved.current) return;
    saved.current = json;
    latest.current?.(doc);
  };
  const editor = useEditor({
    extensions: extensions(placeholder),
    content: value ?? '',
    autofocus: autoFocus ? 'end' : false,
    editable,
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (
          latestSubmit.current &&
          event.key === 'Enter' &&
          !event.shiftKey &&
          !event.altKey &&
          !event.isComposing
        ) {
          event.preventDefault();
          latestSubmit.current();
          return true;
        }
        return false;
      },
      attributes: {
        'aria-label': ariaLabel ?? 'Conteúdo',
        class: cn(proseClass, compact && 'max-h-36 overflow-y-auto overscroll-contain'),
        style: `min-height:${minHeight}px`,
      },
    },
    onCreate: ({ editor: e }) => {
      saved.current = JSON.stringify(e.getJSON());
    },
    onUpdate: ({ editor: e }) => {
      latestChange.current?.(e.getJSON() as RichText);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => flush(e), 1200);
    },
    onBlur: ({ editor: e }) => timer.current && flush(e),
  });
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;
  return (
    <div
      className={cn(
        editable &&
          !compact &&
          'rounded-panel bg-canvas/40 shadow-[inset_0_0_0_1px_var(--color-line)] focus-within:shadow-[inset_0_0_0_1px_var(--color-line-strong)]',
        className,
      )}
    >
      {editable && showToolbar && <Toolbar editor={editor} />}
      <EditorContent editor={editor} className={cn(editable && (compact ? 'px-2 py-1' : 'px-4 py-3'))} />
    </div>
  );
}
